import { apiRequest } from '../../../services/api/client';
import type { ClassRecord, Trimester } from '../context/TrimestersContext';

type QuarterResource = {
  id: number;
  name: string;
  year: number;
  quarter_number: 1 | 2 | 3 | 4;
  starts_on: string | null;
  ends_on: string | null;
  active: boolean;
  magazines_count?: number;
  class_quarters_count?: number;
};

type QuarterListResponse = {
  data: QuarterResource[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    has_more_pages: boolean;
  };
};

type QuarterResponse = {
  data: QuarterResource;
};

type ClassResource = {
  id: number;
  name: string;
  segment_id: number;
  gender: string;
  teacher_names: string[];
  teacher_member_ids: number[];
  description: string | null;
  active: boolean;
  quarter_id: number;
  students_count?: number;
};

type ClassesListResponse = {
  data: ClassResource[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    has_more_pages: boolean;
  };
};

const toClass = (classResource: ClassResource): ClassRecord => ({
  id: String(classResource.id),
  title: classResource.name,
  category: '', // será preenchido depois se necessário
  teachers: classResource.teacher_names,
  students: classResource.students_count ?? 0,
  avg: '0%', // será calculado depois se necessário
  ageRange: '', // será preenchido depois se necessário
  gender: classResource.gender,
  type: 'Normal', // padrão
  room: '', // será preenchido depois se necessário
  schedule: '', // será preenchido depois se necessário
  segmentId: String(classResource.segment_id),
  teacherMemberIds: classResource.teacher_member_ids.map(String),
  active: classResource.active,
});

export interface SaveTrimesterInput {
  year: number;
  number: 1 | 2 | 3 | 4;
  startDate: string;
  endDate: string;
  active?: boolean;
}

const buildDefaultLabel = (number: number, year: number) => `${number}º Trimestre ${year}`;

const normalizeDate = (value?: string | null) => {
  if (!value) return '';
  return value.slice(0, 10);
};

const toTrimester = (quarter: QuarterResource): Trimester => ({
  id: String(quarter.id),
  year: quarter.year,
  number: quarter.quarter_number,
  label: quarter.name || buildDefaultLabel(quarter.quarter_number, quarter.year),
  startDate: normalizeDate(quarter.starts_on),
  endDate: normalizeDate(quarter.ends_on),
  active: quarter.active,
  magazinesCount: quarter.magazines_count ?? 0,
  classQuartersCount: quarter.class_quarters_count ?? 0,
});

const toPayload = (input: SaveTrimesterInput) => ({
  name: buildDefaultLabel(input.number, input.year),
  year: input.year,
  quarter_number: input.number,
  starts_on: input.startDate || null,
  ends_on: input.endDate || null,
  active: input.active ?? true,
});

export interface ReplicateOptions {
  include_classes?: boolean;
  include_magazines?: boolean;
  include_students?: boolean;
  excluded_student_ids?: string[];
  selected_class_ids?: string[];
  include_teachers?: boolean;
}

export const trimestersService = {
  async list() {
    const firstPage = await apiRequest<QuarterListResponse>('/ebd/quarters?per_page=100');
    return firstPage.data.map(toTrimester);
  },

  async create(input: SaveTrimesterInput) {
    const payload = await apiRequest<QuarterResponse>('/ebd/quarters', {
      method: 'POST',
      body: JSON.stringify(toPayload(input)),
    });

    return toTrimester(payload.data);
  },

  async update(id: string, input: SaveTrimesterInput) {
    const payload = await apiRequest<QuarterResponse>(`/ebd/quarters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(toPayload(input)),
    });

    return toTrimester(payload.data);
  },

  async delete(id: string) {
    await apiRequest<void>(`/ebd/quarters/${id}`, {
      method: 'DELETE',
    });
  },

  async replicate(sourceId: string, targetId: string, options: ReplicateOptions = {}) {
    const payload = await apiRequest<{ data: { classes: number; magazines: number; students: number } }>(
      `/ebd/quarters/${sourceId}/replicate/${targetId}`,
      {
        method: 'POST',
        body: JSON.stringify(options),
      }
    );

    return payload.data;
  },

  async getClasses(quarterId?: string) {
    if (!quarterId) {
      return [];
    }

    const params = new URLSearchParams();
    params.append('quarter_id', quarterId);
    params.append('per_page', '100');

    const response = await apiRequest<ClassesListResponse>(`/ebd/classes?${params.toString()}`);
    return response.data.map(toClass);
  },
};
